import type { Matter, MatterContactConnection } from "@/lib/types";
import {
  getlabel,
  isClientPartyConnectionLabel,
  isDefendantPartyConnectionLabel,
  matterContactHasClientPartyRole,
  matterContactHasDefendantPartyRole,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";

export type MatterContactHeroTone = "green" | "gray" | "blue" | "provider";

/**
 * Hero tone from a single connection **label** (fallback when full `Matter` is not
 * available). Client / defendant party labels only; everything else is gray.
 */
export function matterContactConnectionHeroTone(
  label: string,
): MatterContactHeroTone {
  if (isClientPartyConnectionLabel(label)) {
    return "green";
  }
  if (label === "Provider") {
    return "provider";
  }
  if (isDefendantPartyConnectionLabel(label)) {
    return "blue";
  }
  return "gray";
}

/**
 * Hero tone from all matter contact connections for this **contact** on the
 * matter: if any connection is Client → green; else if any is Defendant → blue;
 * else gray. Does not use contact record subtypes.
 */
export function matterContactConnectionHeroToneFromMatterConnection(
  matter: Matter,
  connection: MatterContactConnection,
): MatterContactHeroTone {
  const { contactId } = connection;
  const label = getlabel(connection);

  if (matterContactHasClientPartyRole(matter, contactId)) {
    return "green";
  }
  if (label === "Provider") {
    return "provider";
  }
  if (matterContactHasDefendantPartyRole(matter, contactId)) {
    return "blue";
  }
  return "gray";
}

/**
 * Gradient shells for matter contact heroes — same visual system as
 * `MatterClientHeroPreviewCard` / compact related-contact previews.
 */
export const MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS =
  "relative overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(52,211,153,0.30),_transparent_42%),linear-gradient(180deg,_#0B9B6E_0%,_#08795A_100%)] p-5 shadow-sm";

export const MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS =
  "relative overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.25),_transparent_42%),linear-gradient(180deg,_#475569_0%,_#334155_100%)] p-5 shadow-sm";

export const MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS =
  "relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1a1f4e] to-[#0f1235] p-5 shadow-sm";

export const MATTER_PROVIDER_HERO_GRADIENT_PANEL_CLASS =
  "relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#6B7AA6] to-[#5A6994] p-5 shadow-sm";

/** Neutral shell for `MatterContactConnectionRichCard` when `visualVariant` is `white`. */
export const MATTER_CONTACT_CONNECTION_RICH_CARD_WHITE_PANEL_CLASS =
  "bg-card relative overflow-hidden rounded-[28px] border border-border p-5 shadow-sm";

export function matterContactHeroToneGradientPanelClass(
  tone: MatterContactHeroTone,
): string {
  switch (tone) {
    case "green":
      return MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS;
    case "gray":
      return MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS;
    case "blue":
      return MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS;
    case "provider":
      return MATTER_PROVIDER_HERO_GRADIENT_PANEL_CLASS;
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

/** Accent for the white lead-icon tile on `MatterContactConnectionRichCard`. */
export const MATTER_CONTACT_HERO_TONE_LEAD_ICON_CLASS: Record<
  MatterContactHeroTone,
  string
> = {
  green: "text-[#08795A]",
  gray: "text-[#475569]",
  blue: "text-[#1a1f4e]",
  provider: "text-[#6B7AA6]",
};
